import * as React from "react";
import { UseQueryResult } from "@tanstack/react-query";

import { getApiErrorMessage } from "api/apiError";
import { FleetHistory, FleetIncident, FleetTenant } from "api/fleet";
import { isConnected, TenantIdentity } from "Components/Tenants";
import { Badge, Card, CardHeader, EmptyState, ErrorState, Skeleton } from "Components/ui";
import { CircleCheck, Hourglass } from "Components/ui/icons";
import { cn } from "utils/cn";
import { formatDateTime, pluralize } from "utils/format";
import { formatDuration, formatWhen } from "../format";

// Outages are what went wrong; restarts are listed apart because a restart on its own (every
// deploy restarts the app) isn't an outage. Three or more in a day reads as a crash loop.
const OUTAGE = {
  down: { label: "Down", dot: "bg-danger", text: "text-danger-text" },
  process: { label: "pm2 process offline", dot: "bg-warning", text: "text-warning-text" },
} as const;
const RESTART_LOOP = 3;
const MAX_OUTAGES = 4;
const MAX_RESTARTS = 5;

interface RestartGroup {
  tenantId: string;
  name: string;
  slug: string;
  count: number;
  lastAt: string;
  /** An outage of the same tenant overlaps one of its restarts (within a sample interval). */
  withOutage: boolean;
}

const toMs = (value: string) => new Date(value).getTime();

const groupRestarts = (
  incidents: FleetIncident[],
  outages: FleetIncident[],
  sampleMs: number,
  now: number
): RestartGroup[] => {
  const groups = new Map<string, RestartGroup>();
  incidents
    .filter((incident) => incident.kind === "restart")
    .forEach((restart) => {
      const at = toMs(restart.startedAt);
      const withOutage = outages.some(
        (outage) =>
          outage.tenantId === restart.tenantId &&
          toMs(outage.startedAt) <= at &&
          (outage.endedAt ? toMs(outage.endedAt) : now) >= at - sampleMs
      );
      const group = groups.get(restart.tenantId);
      if (group) {
        group.count += restart.count || 1;
        group.lastAt = restart.startedAt > group.lastAt ? restart.startedAt : group.lastAt;
        group.withOutage = group.withOutage || withOutage;
      } else {
        groups.set(restart.tenantId, {
          tenantId: restart.tenantId,
          name: restart.name,
          slug: restart.slug,
          count: restart.count || 1,
          lastAt: restart.startedAt,
          withOutage,
        });
      }
    });
  return Array.from(groups.values()).sort((a, b) => (a.lastAt < b.lastAt ? 1 : -1));
};

const SectionTitle: React.FC<{ title: string; count: number }> = ({ title, count }) => (
  <div className="mb-3 flex items-center gap-3">
    <h4 className="text-sm font-medium">{title}</h4>
    <Badge>{count}</Badge>
    <span aria-hidden="true" className="h-px flex-1 bg-border" />
  </div>
);

interface IncidentsCardProps {
  history: UseQueryResult<FleetHistory>;
  /** Live tenants, for each event's URL and TeleOpd link. */
  tenants: FleetTenant[];
}

/** The sampler's last 24 hours: outages first, then restarts grouped per tenant. */
const IncidentsCard: React.FC<IncidentsCardProps> = ({ history, tenants }) => {
  const data = history.data;
  const tenantsById = React.useMemo(
    () => new Map(tenants.map((tenant) => [tenant.tenantId, tenant])),
    [tenants]
  );
  const outages = React.useMemo(
    () => (data ? data.incidents.filter((incident) => incident.kind !== "restart") : []),
    [data]
  );
  const restarts = React.useMemo(
    () =>
      data
        ? groupRestarts(data.incidents, outages, data.sampleMinutes * 60 * 1000, Date.now())
        : [],
    [data, outages]
  );
  // With no samples yet, "no outages" would be a guess.
  const sampled = Boolean(
    data && (data.hostSeries.length > 0 || data.tenants.some((tenant) => tenant.samples > 0))
  );
  const ongoing = outages.filter((outage) => !outage.endedAt).length;
  const restartTotal = restarts.reduce((sum, group) => sum + group.count, 0);

  // Unconnected tenants are greyed by color (not opacity) so the text stays readable.
  const identity = (tenantId: string, name: string, slug: string) => {
    const tenant = tenantsById.get(tenantId);
    return (
      <TenantIdentity
        name={name}
        slug={slug}
        uri={tenant ? tenant.uri : null}
        className={cn("text-sm", !isConnected(tenant?.link) && "text-muted-foreground")}
      />
    );
  };

  let body: React.ReactNode;
  if (history.isLoading) {
    body = (
      <div className="space-y-4">
        {[0, 1, 2].map((index) => (
          <div key={index} className="space-y-2">
            <Skeleton className="h-4 w-1/3" />
            <Skeleton className="h-4 w-3/4" />
          </div>
        ))}
      </div>
    );
  } else if (history.isError) {
    body = (
      <ErrorState
        compact
        title="Outages and restarts didn't load"
        description={getApiErrorMessage(history.error)}
      />
    );
  } else if (!data || !sampled) {
    body = (
      <EmptyState
        compact
        icon={<Hourglass />}
        title="Collecting data"
        description="Outages and restarts appear once SAP has sampled the fleet for a few minutes."
      />
    );
  } else {
    const now = Date.now();
    body = (
      <div className="space-y-6">
        <section>
          <SectionTitle title="Outages" count={outages.length} />
          {outages.length === 0 ? (
            <div className="flex items-start gap-2.5">
              <CircleCheck aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0 text-success" />
              <div>
                <p className="text-sm font-medium">No outages</p>
                <p className="text-xs text-muted-foreground">
                  No failed health check and no pm2 process offline.
                </p>
              </div>
            </div>
          ) : (
            <ul className="space-y-4">
              {outages.slice(0, MAX_OUTAGES).map((outage) => {
                const kind = OUTAGE[outage.kind === "down" ? "down" : "process"];
                const end = outage.endedAt ? toMs(outage.endedAt) : now;
                const lasted = formatDuration((end - toMs(outage.startedAt)) / 1000);
                return (
                  <li
                    key={`${outage.tenantId}-${outage.kind}-${outage.startedAt}`}
                    className="flex items-start gap-3"
                  >
                    <span
                      aria-hidden="true"
                      className={cn("mt-1.5 h-2 w-2 shrink-0 rounded-full", kind.dot)}
                    />
                    <div className="min-w-0 flex-1 space-y-1">
                      <div className="flex items-baseline justify-between gap-3">
                        <p className={cn("text-sm font-medium", kind.text)}>{kind.label}</p>
                        <time
                          dateTime={outage.startedAt}
                          title={formatDateTime(outage.startedAt)}
                          className="shrink-0 text-xs tabular-nums text-muted-foreground"
                        >
                          {formatWhen(outage.startedAt, now)}
                        </time>
                      </div>
                      {identity(outage.tenantId, outage.name, outage.slug)}
                      <p className="text-xs text-muted-foreground">
                        {outage.endedAt ? (
                          `${outage.detail} · lasted ${lasted}`
                        ) : (
                          <span className="font-medium text-danger-text">Ongoing for {lasted}</span>
                        )}
                      </p>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
          {outages.length > MAX_OUTAGES && (
            <p className="mt-3 text-xs text-muted-foreground">
              {pluralize(outages.length - MAX_OUTAGES, "earlier outage")} not shown
            </p>
          )}
        </section>

        <section>
          <SectionTitle title="Restarts" count={restartTotal} />
          {restarts.length === 0 ? (
            <p className="text-sm text-muted-foreground">No pm2 restarts.</p>
          ) : (
            <ul className="space-y-4">
              {restarts.slice(0, MAX_RESTARTS).map((group) => (
                <li key={group.tenantId} className="flex items-start justify-between gap-3">
                  <div className="min-w-0 space-y-1">
                    {identity(group.tenantId, group.name, group.slug)}
                    <p
                      className={cn(
                        "text-xs",
                        group.withOutage ? "text-danger-text" : "text-muted-foreground"
                      )}
                    >
                      {group.withOutage
                        ? "Went down around the restart"
                        : "Health checks kept passing"}
                    </p>
                  </div>
                  <div className="shrink-0 text-right text-xs">
                    <time
                      dateTime={group.lastAt}
                      title={formatDateTime(group.lastAt)}
                      className="block font-medium tabular-nums"
                    >
                      {formatWhen(group.lastAt, now)}
                    </time>
                    <p
                      className={cn(
                        "mt-0.5",
                        group.count >= RESTART_LOOP ? "text-warning-text" : "text-muted-foreground"
                      )}
                    >
                      {pluralize(group.count, "restart")}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          )}
          {restarts.length > MAX_RESTARTS && (
            <p className="mt-3 text-xs text-muted-foreground">
              {pluralize(restarts.length - MAX_RESTARTS, "more tenant")} restarted
            </p>
          )}
        </section>
      </div>
    );
  }

  return (
    <Card>
      <CardHeader
        title="Outages and restarts"
        description={
          data
            ? `Last ${data.hours} hours · sampled every ${data.sampleMinutes} min`
            : "Last 24 hours"
        }
        actions={
          ongoing > 0 ? (
            <Badge tone="danger" dot>
              {ongoing} ongoing
            </Badge>
          ) : undefined
        }
      />
      <div className="px-5 pb-5 pt-4">{body}</div>
    </Card>
  );
};

export default IncidentsCard;
